import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  useWindowDimensions,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import profileDetailStyles from '../theme/profileDetailStyles';
import api from '../services/api';
import {
  COUNTRY_OPTIONS,
  COUNTRY_CODE_OPTIONS,
  STATE_OPTIONS,
} from '../constants/profileOptions';

const ContactTabScreen = ({ memberId, isOwnProfile, isMediatorProfile, onEditStateChange }) => {
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  const scrollViewRef = useRef(null);
  const fieldPositions = useRef({});
  
  const [contactData, setContactData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeDropdownField, setActiveDropdownField] = useState(null);
  const [activeDropdownOptions, setActiveDropdownOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadContactData();
  }, [memberId]);

  useEffect(() => {
    if (isEditMode && onEditStateChange && Object.keys(editData).length > 0) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: true,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  }, [editData, isEditMode]);

  const loadContactData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/${memberId}/contact`);
      const data = response.data?.data || response.data;
      // Always set data even if null/empty to allow editing
      setContactData(data || {});
    } catch (error) {
      console.error('Error loading contact data:', error);
      // Set empty object to allow editing even on error
      setContactData({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    const contact = contactData?.contactDetails || contactData || {};
    setEditData(contact);
    setIsEditMode(true);
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: false,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
    setValidationErrors({});
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: false,
        hasUnsavedChanges: false,
        onSave: null,
        onCancel: null,
      });
    }
  };

  const getMandatoryFields = () => [
    { key: 'presentAddress', label: 'Address (Door No/Flat No, etc.)' },
    { key: 'presentCity', label: 'Town/City/Village' },
    { key: 'presentState', label: 'State' },
    { key: 'presentPinCode', label: 'PIN' },
    { key: 'primaryContactPersonName', label: 'Contact Person Name' },
    { key: 'primaryEmail', label: 'Email' },
    { key: 'primaryCountryCode', label: 'Country Code' },
    { key: 'primaryPhoneNumber', label: 'Phone Number' },
  ];

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[0-9]{10}$/; // 10 digit phone number
    return phoneRegex.test(phone.replace(/\s/g, '')); // Remove spaces before validation
  };

  const validateFields = () => {
    const errors = {};

    // Validate mandatory fields
    const mandatoryFields = [
      { key: 'presentAddress', label: 'Address (Door No/Flat No, etc.)' },
      { key: 'presentCity', label: 'Town/City/Village' },
      { key: 'presentState', label: 'State' },
      { key: 'presentPinCode', label: 'PIN' },
      { key: 'primaryContactPersonName', label: 'Contact Person Name' },
      { key: 'primaryEmail', label: 'Email' },
      { key: 'primaryCountryCode', label: 'Country Code' },
      { key: 'primaryPhoneNumber', label: 'Phone Number' },
    ];

    mandatoryFields.forEach(field => {
      const value = field.key in editData ? editData[field.key] : (contactData?.contactDetails?.[field.key] || contactData?.[field.key]);
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors[field.key] = `${field.label} is required`;
      }
    });

    // Validate primary email
    if (editData.primaryEmail && !validateEmail(editData.primaryEmail)) {
      errors.primaryEmail = 'Invalid email format';
    }

    // Validate secondary email
    if (editData.secondaryEmail && !validateEmail(editData.secondaryEmail)) {
      errors.secondaryEmail = 'Invalid email format';
    }

    // Validate primary phone number
    if (editData.primaryPhoneNumber && !validatePhoneNumber(editData.primaryPhoneNumber)) {
      errors.primaryPhoneNumber = 'Invalid phone number (10 digits required)';
    }

    // Validate secondary phone number
    if (editData.secondaryPhoneNumber && !validatePhoneNumber(editData.secondaryPhoneNumber)) {
      errors.secondaryPhoneNumber = 'Invalid phone number (10 digits required)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate before saving
    if (!validateFields()) {
      // Scroll to first error field
      if (scrollViewRef.current && Object.keys(validationErrors).length > 0) {
        const firstErrorKey = Object.keys(validationErrors)[0];
        const errorPosition = fieldPositions.current[firstErrorKey];
        if (errorPosition !== undefined) {
          scrollViewRef.current.scrollTo({ y: Math.max(0, errorPosition - 100), animated: true });
        } else {
          scrollViewRef.current.scrollTo({ y: 0, animated: true });
        }
      }
      
      Alert.alert('Validation Error', 'Please fix the errors before saving');
      return false;
    }

    setIsSaving(true);
    try {
      const response = await api.put(`/api/EnhancedMember/${memberId}/contact`, editData);
      setContactData(response.data?.data || response.data);
      Alert.alert('Success', 'Contact details updated successfully');
      setIsEditMode(false);
      setEditData({});
      setValidationErrors({});
      if (onEditStateChange) {
        onEditStateChange({
          isEditing: false,
          hasUnsavedChanges: false,
          onSave: null,
          onCancel: null,
        });
      }
      return true;
    } catch (error) {
      console.error('Error saving contact data:', error);
      Alert.alert('Error', error.response?.data?.message || 'Failed to save changes');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const getOptionsForField = (fieldKey) => {
    const optionsMap = {
      primaryCountryCode: COUNTRY_CODE_OPTIONS,
      secondaryCountryCode: COUNTRY_CODE_OPTIONS,
      presentState: STATE_OPTIONS,
      permanentState: STATE_OPTIONS,
      presentCountry: COUNTRY_OPTIONS,
      permanentCountry: COUNTRY_OPTIONS,
    };
    return optionsMap[fieldKey] || null;
  };

  const openDropdown = (fieldKey, label) => {
    const options = getOptionsForField(fieldKey);
    if (options) {
      setActiveDropdownField({ key: fieldKey, label });
      setActiveDropdownOptions(options);
      setDropdownVisible(true);
    }
  };

  const selectDropdownOption = (value) => {
    if (activeDropdownField) {
      setEditData({ ...editData, [activeDropdownField.key]: value });
    }
    setDropdownVisible(false);
    setActiveDropdownField(null);
  };

  const renderSectionTitle = (title) => (
    <Text style={profileDetailStyles.sectionHeader}>{title}</Text>
  );

  const renderInfoRow = (label, value, fieldKey, editable = true) => {
    const isEditing = isEditMode && editable;
    const options = getOptionsForField(fieldKey);
    const hasDropdown = options !== null;
    const isMandatory = getMandatoryFields().some(f => f.key === fieldKey);
    const hasError = validationErrors[fieldKey];
    const displayLabel = isEditing && isMandatory ? `${label} *` : label;
    
    if (isEditing) {
      return (
        <View 
          style={profileDetailStyles.inputWrapper}
          onLayout={(event) => {
            fieldPositions.current[fieldKey] = event.nativeEvent.layout.y;
          }}
        >
          <Text style={profileDetailStyles.floatingLabel}>{displayLabel}</Text>
          {hasDropdown ? (
            <TouchableOpacity
              style={[profileDetailStyles.dropdown, hasError && profileDetailStyles.inputError]}
              onPress={() => openDropdown(fieldKey, label)}
            >
              <Text style={editData[fieldKey] ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {fieldKey in editData ? (editData[fieldKey] || `Select ${label}`) : (value || `Select ${label}`)}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          ) : (
            <TextInput
              style={[
                profileDetailStyles.input,
                focusedInput === fieldKey && profileDetailStyles.inputFocused,
                hasError && profileDetailStyles.inputError
              ]}
              value={fieldKey in editData ? (editData[fieldKey]?.toString() || '') : (value?.toString() || '')}
              onChangeText={(text) => {
                setEditData({ ...editData, [fieldKey]: text });
                // Clear error when user starts typing
                if (hasError) {
                  setValidationErrors({ ...validationErrors, [fieldKey]: undefined });
                }
              }}
              onFocus={() => setFocusedInput(fieldKey)}
              onBlur={() => setFocusedInput(null)}
              placeholder={`Enter ${label}`}
              placeholderTextColor={colors.textSecondary}
              keyboardType={fieldKey.includes('Phone') ? 'phone-pad' : fieldKey.includes('Email') ? 'email-address' : 'default'}
              autoCapitalize={fieldKey.includes('Email') ? 'none' : 'sentences'}
            />
          )}
          {hasError && (
            <Text style={profileDetailStyles.errorText}>{hasError}</Text>
          )}
        </View>
      );
    } else {
      return (
        <View style={profileDetailStyles.infoItem}>
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
            <View style={profileDetailStyles.viewModeContainer}>
              <Text style={profileDetailStyles.viewModeText}>{value || 'Not Provided'}</Text>
            </View>
          </View>
        </View>
      );
    }
  };

  if (isLoading) {
    return (
      <View style={profileDetailStyles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={profileDetailStyles.loadingText}>Loading contact details...</Text>
      </View>
    );
  }

  if (!contactData) {
    return (
      <View style={profileDetailStyles.container}>
        <Text style={profileDetailStyles.noDataText}>No contact details data available</Text>
      </View>
    );
  }

  const contact = contactData.contactDetails || contactData;

  return (
    <View style={profileDetailStyles.container}>
      <ScrollView 
        ref={scrollViewRef}
        style={profileDetailStyles.scrollView} 
        contentContainerStyle={profileDetailStyles.scrollContent}
      >
        {renderSectionTitle('Present Address')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Address (Door No/ Flat No, etc.)', contact.presentAddress, 'presentAddress')}
          {renderInfoRow('Area', contact.presentArea, 'presentArea')}
          {renderInfoRow('Town/ City/ Village', contact.presentCity, 'presentCity')}
          {renderInfoRow('State', contact.presentState, 'presentState')}
          {renderInfoRow('PIN', contact.presentPinCode, 'presentPinCode')}
          {renderInfoRow('Country', contact.presentCountry, 'presentCountry')}
        </View>

        {renderSectionTitle('Permanent Address')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Address (Door No/ Flat No, etc.)', contact.permanentAddress, 'permanentAddress')}
          {renderInfoRow('Area', contact.permanentArea, 'permanentArea')}
          {renderInfoRow('Town/ City/ Village', contact.permanentCity, 'permanentCity')}
          {renderInfoRow('State', contact.permanentState, 'permanentState')}
          {renderInfoRow('PIN', contact.permanentPinCode, 'permanentPinCode')}
          {renderInfoRow('Country', contact.permanentCountry, 'permanentCountry')}
        </View>

        {renderSectionTitle('Primary Contact')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Contact Person Name', contact.primaryContactPersonName, 'primaryContactPersonName')}
          {renderInfoRow('Email', contact.primaryEmail, 'primaryEmail')}
          {renderInfoRow('Country Code', contact.primaryCountryCode, 'primaryCountryCode')}
          {renderInfoRow('Phone Number', contact.primaryPhoneNumber, 'primaryPhoneNumber')}
        </View>

        {renderSectionTitle('Secondary Contact (Alternate Contact)')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Contact Person Name', contact.secondaryContactPersonName, 'secondaryContactPersonName')}
          {renderInfoRow('Email', contact.secondaryEmail, 'secondaryEmail')}
          {renderInfoRow('Country Code', contact.secondaryCountryCode, 'secondaryCountryCode')}
          {renderInfoRow('Phone Number', contact.secondaryPhoneNumber, 'secondaryPhoneNumber')}
        </View>
      </ScrollView>

      {/* Floating Edit Buttons */}
      {(isOwnProfile || isMediatorProfile) && (
        <View style={profileDetailStyles.floatingButtonContainer}>
          {isEditMode ? (
            <>
              <TouchableOpacity 
                onPress={handleCancelEdit} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingCancelButton]}
                disabled={isSaving}
              >
                <SvgIcon name="close" size={16} color={colors.white} />
              </TouchableOpacity>
              <TouchableOpacity 
                onPress={handleSave} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingSaveButton]}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <SvgIcon name="checkmark" size={16} color={colors.white} />
                )}
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity 
              onPress={handleEdit} 
              style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingEditButton]}
            >
              <SvgIcon name="create-outline" size={16} color={colors.white} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Dropdown Modal */}
      <Modal
        visible={dropdownVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity 
          style={profileDetailStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={profileDetailStyles.modalContent}>
            <View style={profileDetailStyles.modalHeader}>
              <Text style={profileDetailStyles.modalTitle}>
                {activeDropdownField?.label || 'Select'}
              </Text>
              <TouchableOpacity onPress={() => setDropdownVisible(false)}>
                <SvgIcon name="close" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            <ScrollView style={profileDetailStyles.modalScroll}>
              {activeDropdownOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={profileDetailStyles.modalItem}
                  onPress={() => selectDropdownOption(option.value)}
                >
                  <Text style={profileDetailStyles.modalItemText}>{option.label}</Text>
                  {(editData[activeDropdownField?.key] || contact?.[activeDropdownField?.key]) === option.value && (
                    <SvgIcon name="checkmark-circle" size={20} color={colors.accent} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default ContactTabScreen;
